<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import type { GarageItem, ResearchNode } from '@/net/api';
import { askToBuy } from '@/state/confirm-purchase';
import { clearResearchFocus, clearResearchFocusIf, researchFocus } from '@/state/research-focus';
import CurrencyIcon from '@/ui/CurrencyIcon.vue';
import { formatCredits } from './stat-display';
import { formatCountdown } from './format';
import {
  clampPan,
  clampZoom,
  DRAG_THRESHOLD_PX,
  fitView,
  panToReveal,
  pinchView,
  wheelZoom,
  zoomAbout,
  type Rect,
  type Size,
  type Vec,
} from './pan-zoom';

/**
 * The research tree, laid out as a tech tree.
 *
 * **A row is a tier, and a node's children stand side by side in the row
 * immediately below it**, contiguous, with the parent centred over the block of
 * them — the classic tidy-tree (Reingold–Tilford) assignment, read top to
 * bottom. Five roots means five sub-trees standing beside each other across the
 * page, one per branch. Everything it draws comes off the nodes themselves —
 * `branch`, `branchName`, `branchOrder`, `tier` and `prerequisiteIds` are
 * catalogue data resolved server-side — so adding a fifth branch is a change to
 * `catalogue.data.ts` and nothing here.
 *
 * **It runs downward, and that is the whole orientation.** Tier 1 is the top
 * row, and depth increases toward the bottom of the page. It used to run left to
 * right, a tier per column; every axis in this file is that layout transposed,
 * which is why the tidy-tree trick below reads as it does.
 *
 * **It works because the catalogue is a real tree.** Every node has at most one
 * prerequisite and that prerequisite is in the same branch, both asserted in
 * `stat-resolution.test.ts`. Seven nodes used to take two, three of them
 * reaching across branches, and that is a DAG: a node with parents in two
 * branches has to sit beside both, so some edge has to cut diagonally across the
 * middle of the grid. `catalogue.data.ts` records exactly what was cut and why.
 * If a second prerequisite ever comes back, this file still draws its edge —
 * honestly, as a line that crosses — rather than hiding it.
 *
 * **Columns are assigned, not implied.** Each leaf owns one grid column; each
 * internal node *spans* the columns of its whole sub-tree and is
 * `justify-self: center` within them, which is what puts a parent over the
 * middle of its children without this file ever knowing how *wide* a card is.
 * It is also what makes the layout provably crossing-free: a parent's bracket is
 * contained in the x-range of its own children, and sibling blocks are disjoint
 * by construction.
 *
 * **The connectors are brackets, and they are measured.** A stub down from the
 * parent, one horizontal spine, a stub down into each child. The rects come from
 * the laid-out DOM rather than from assumed geometry, because the cards are
 * content-sized — a hardcoded column width would be wrong the moment the font,
 * the zoom or a two-line name changed.
 *
 * **The branch name rides on the sub-tree's root, not on a column header.** A
 * branch owns a contiguous *block* of columns rather than one, so a header over
 * a single column would caption whichever lineage happened to start there — the
 * same objection the old layout's row labels had, on the new axis. And a header
 * centred over the whole block would land exactly where the root card already
 * is. So the name (and the branch's progress count) stays on the entry node that
 * opens the branch, which is the one place it is true. The tier captions down
 * the left edge are a different thing and are honest: a row really is one tier.
 *
 * ## It is panned and zoomed, not scrolled
 *
 * The tree is 2,068 × 724 px, so on anything smaller than a wide monitor it does
 * not fit — and it used to answer that with two scrollbars, which is a poor way
 * to read a map. It is a viewport now: **drag anywhere to
 * move around it, wheel to zoom toward whatever the pointer is over**, the way a
 * passive tree is read. The arithmetic — the clamps, the zoom-about-a-point, the
 * pan bounds and the reveal — is `pan-zoom.ts`, which is pure and unit-tested;
 * this file owns the listeners, the one `transform` those numbers feed, and the
 * five properties below.
 *
 * - **Zoom is a CSS `transform: scale()` on a wrapper, never a re-layout.**
 *   Growing the fonts or the grid tracks would reflow thirty-four content-sized
 *   cards on every wheel notch, and — the part that actually breaks — it would
 *   *move the cards*, which the connectors are measured from. Under a transform
 *   the layout is fixed, so `measure()` runs when the data or the box changes and
 *   never when the zoom does: the brackets are measured once and are correct at
 *   every scale. Everything that reads a `getBoundingClientRect` here therefore
 *   divides by the zoom, because a rect under a scaled ancestor is in screen
 *   pixels and every one of these measurements is a statement about the layout.
 * - **The wheel listener is registered by hand, `{ passive: false }`.** Zooming
 *   has to `preventDefault` or the wheel scrolls the garage behind the tab, and a
 *   passive listener may not — Vue's `@wheel` is passive by default in some
 *   setups and the failure is silent, so this one is added in `onMounted` and
 *   removed on teardown rather than being written in the template.
 * - **A drag needs a threshold, because a click on a card is how a node is
 *   read.** `DRAG_THRESHOLD_PX` and the argument for a threshold rather than a
 *   second button are in `pan-zoom.ts`; what belongs here is the consequence,
 *   which is that a press that *did* become a drag must have its `click`
 *   swallowed in the capture phase. Otherwise a pan that happens to end on the
 *   panel's Research button opens a purchase dialog.
 * - **The viewport is `overflow: hidden` and must never actually scroll.** A
 *   hidden box is still a scroll container, so the browser will happily scroll it
 *   to reveal a focused card — and then the transform and what is on screen
 *   disagree about where every card is, which is exactly the bug the whole
 *   measured-connector design is trying to avoid. `onViewportScroll` puts it
 *   straight back to zero, and the reveal is done properly instead: explicitly,
 *   as a pan, on `focusin`. Keyboard users can still reach all thirty-four cards.
 * - **The pan is bounded so the tree cannot be lost**, and there is a Fit control
 *   for when a player would rather start again. The tab also *opens* fitted (at
 *   1:1 or below, never magnified), which is the whole point of the change: on a
 *   laptop the first thing you see is the shape of the tree rather than the
 *   top-left corner of it.
 *
 * ## The shop can point at a node
 *
 * `state/research-focus` carries a node id from a hull or turret card that named
 * the gate blocking it. When one arrives the card is ringed and scrolled to,
 * **once, when the focus arrives** — never on a re-render, because a highlight
 * that re-scrolled would fight the rule below that opening a panel moves
 * nothing. It is cleared on unmount ("left the page") and when the node the shop
 * pointed at is observed running ("bought it").
 *
 * ## A card is a picture, a name and a price
 *
 * And nothing else. Everything a player might *read* — the blurb, what the node
 * unlocks, the passive it grants, how long it takes and the button that starts
 * it — lives in a panel that opens on hover or focus. Carried inline, as it was,
 * thirty-four cards were thirty-four paragraphs and the tree read as a wall of text
 * rather than as a map: the shape the grid exists to show was the one thing you
 * could not see.
 *
 * Three properties of that panel are load-bearing, and each is a bug if undone.
 *
 * - **It is positioned over the tree, never in the flow.** `place()` puts it at
 *   measured coordinates inside `.grid`, which is the only positioned ancestor
 *   between the two, so opening one moves no card. In the flow it would reflow
 *   the grid under the pointer — the same mistake the garage lobby's notices are
 *   written to avoid, and worse here because the edges are measured from where
 *   the cards ended up.
 * - **Hover alone would be unreachable.** A pointer is not the only way to read
 *   a card: a keyboard has no hover at all and a touchscreen's is a tap. The
 *   card is therefore a `<button>` in the disclosure sense — `aria-expanded`
 *   over `aria-controls` — and focus and click open exactly what the pointer
 *   opens. Nothing is behind hover *only*.
 * - **The panel is a DOM descendant of the card's slot.** That is what makes
 *   both halves work: `mouseleave` does not fire moving into a descendant, so
 *   the pointer can travel into the panel to press the button, and Tab from the
 *   card lands on that button rather than on the next card three columns away.
 *   Rendering one shared panel at the end of the grid is the obvious
 *   simplification and breaks both.
 */
const props = defineProps<{
  nodes: ResearchNode[];
  items: GarageItem[];
  credits: number;
  busyId: string | null;
}>();

const emit = defineEmits<{
  research: [id: string];
  skip: [id: string];
  /** The countdown reached zero: re-read rather than deciding it is finished. */
  elapsed: [];
}>();

const itemName = (id: string) => props.items.find((i) => i.id === id)?.name ?? id;
const nameOf = (id: string) => props.nodes.find((n) => n.id === id)?.name ?? id;

// ─── Art ─────────────────────────────────────────────────────────────────────
//
// **Derived from the node id**, exactly as part art and map art are:
// `res_gun_drill` -> `/assets/research/res-gun-drill.webp`. A node added to the
// catalogue gets its picture by existing, and there is no second list to keep in
// step with the first.

/** Ids whose art failed to load, so the fallback plate is drawn instead. */
const missingArt = ref(new Set<string>());
function noteMissing(id: string): void {
  // Replaced rather than mutated: a `Set` mutated in place is not reactive.
  missingArt.value = new Set(missingArt.value).add(id);
}

/**
 * A node's picture, or `null` for one that has none.
 *
 * A missing icon must degrade to a plain plate rather than to a broken image —
 * the same honest placeholder a map without art gets, and a prop with
 * `model: null`. That is what makes the tree safe to ship with a generation
 * that failed or a node added ahead of its art.
 */
function artFor(id: string): string | null {
  return missingArt.value.has(id) ? null : `/assets/research/${id.replace(/_/g, '-')}.webp`;
}

/** What the fallback plate carries: enough to tell two plates apart. */
const initials = (name: string) =>
  name
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0] ?? '')
    .join('');

// ─── The wait ────────────────────────────────────────────────────────────────
//
// A node's `secondsRemaining` is a remainder measured at the instant of the
// response and starts ageing immediately, exactly like `secondsToNextFuel`. So
// the rule is the one `format.ts` states for both of them: **count down
// locally, never decide.**
// When the countdown hits zero this asks the parent to re-read; it does not flip
// the node to researched itself, because whether a node is finished is the
// server's answer and a client that guessed would show an unlock that a refusal
// then takes back.

/** When the nodes currently on screen were received. */
const snapshotAtMs = ref(Date.now());
/** Advances once a second, purely to make the countdown re-render. */
const tickMs = ref(Date.now());
let ticker: ReturnType<typeof setInterval> | null = null;
/** Guards against asking for a re-read once per frame until it lands. */
let asked = false;

/** Seconds left on a node right now, aged from the snapshot. */
function remainingOf(node: ResearchNode): number {
  if (node.secondsRemaining === null) return 0;
  const elapsed = Math.floor((tickMs.value - snapshotAtMs.value) / 1000);
  return Math.max(0, node.secondsRemaining - elapsed);
}

const remainingLabel = (node: ResearchNode) => formatCountdown(remainingOf(node));

/**
 * How far through its wait a running node is, 0–1.
 *
 * The border draws this, so it is the one number on the card that has to be a
 * *fraction* rather than a duration — and it is derived from the two figures the
 * server already sends (`durationSeconds`, plus the locally aged
 * `secondsRemaining`) rather than from a new field. Nothing here decides
 * anything: at 1 the card still says "researching" until the re-read comes back
 * with `unlocked`, exactly as the countdown does. A node whose duration the
 * server reports as zero reads as full rather than as a division by zero.
 */
function progressOf(node: ResearchNode): number {
  if (!node.inProgress) return 0;
  if (!(node.durationSeconds > 0)) return 1;
  const done = 1 - remainingOf(node) / node.durationSeconds;
  return Math.max(0, Math.min(1, done));
}

/**
 * One research runs at a time, and the server is the enforcement.
 *
 * Derived from the nodes rather than carried as its own field: "something is
 * running" is already on the wire once per node, and a second way to say it is
 * a second thing to keep in step. The disabled button is the courtesy; the
 * refusal in `GarageService.research` is the rule.
 */
const busyResearching = computed(() => props.nodes.some((n) => n.inProgress));

// ─── Spending ────────────────────────────────────────────────────────────────
//
// Both spends on this tab go through the shared confirmation first. Neither can
// be undone — credits leave the moment a node is started, and a skip is a
// diamond purchase of time already paid for — so the only protection against a
// misplaced click is the click before it. `askToBuy` never rejects, which is
// what lets the guard be one line and the rest of the path stay as it was.
//
// The dialog is mounted once by `GarageScreen`; nothing here renders it.

/**
 * The node this component last asked the server to start.
 *
 * Held so the focus ring can be cleared on *success* rather than on the click:
 * the component does not perform the purchase, so the only evidence a start
 * landed is the node coming back `inProgress` in the next read of `nodes`. The
 * first re-read after the request is the answer either way, so this is cleared
 * on it whether the server agreed or refused.
 */
let startedId: string | null = null;

/**
 * The node's icon as a confirmation-dialog art field, or nothing.
 *
 * Spread into the request rather than returned as a value, so a node with no art
 * contributes no `art` key at all and the dialog takes its text-only shape — the
 * same degradation the card makes when an icon is missing.
 */
function artOf(id: string): { art?: { kind: 'image'; src: string; contain: true } } {
  const src = artFor(id);
  return src ? { art: { kind: 'image', src, contain: true } } : {};
}

async function startResearch(node: ResearchNode): Promise<void> {
  const accepted = await askToBuy({
    // `artFor` already answers null for a node whose icon 404'd earlier in this
    // session, so the dialog inherits the tree's own knowledge of what is on disk
    // rather than discovering it again. `contain`, because these are objects on a
    // dark ground and `cover` would crop a drill bit into an abstraction.
    ...artOf(node.id),
    // A node is started, not bought outright — the credits leave the same way,
    // but "Research `{item}`?" says what is actually happening, and the button
    // below says it again rather than falling back to "Buy".
    verb: 'Research',
    confirmLabel: 'Start research',
    // The wait is part of the price and is the half a credit figure cannot
    // carry — the same argument the panel's "Takes m:ss" line is there for.
    detail: `${node.branchName} · tier ${node.tier} · takes ${formatCountdown(node.durationSeconds)}`,
    item: node.name,
    price: node.cost,
    currency: 'credits',
  });
  if (!accepted) return;
  startedId = node.id;
  emit('research', node.id);
}

/**
 * A double click or a double tap on a card asks to research it — a shortcut to
 * the panel's own button, behind the same confirmation and the same conditions.
 * Timed by hand rather than `dblclick`, which a touch browser does not reliably
 * send for two taps.
 */
const DOUBLE_TAP_MS = 350;
let lastTap: { id: string; at: number } | null = null;
function onSlotClick(node: ResearchNode, slot: EventTarget | null): void {
  openPanel(node.id, slot);
  const now = Date.now();
  const second = lastTap !== null && lastTap.id === node.id && now - lastTap.at <= DOUBLE_TAP_MS;
  lastTap = second ? null : { id: node.id, at: now };
  if (!second) return;
  const startable =
    !node.unlocked &&
    !node.inProgress &&
    node.available &&
    props.credits >= node.cost &&
    props.busyId === null &&
    !busyResearching.value;
  if (startable) void startResearch(node);
}

async function skipWait(node: ResearchNode): Promise<void> {
  const accepted = await askToBuy({
    ...artOf(node.id),
    item: node.name,
    detail: `Finish now, skipping the remaining ${remainingLabel(node)}`,
    // Null while a node is idle, and this button only exists while one is
    // running — the fallback is there so a stale render cannot show `null 💎`.
    price: node.skipDiamondCost ?? 0,
    currency: 'diamonds',
    // Not a "Buy": what changes hands is the wait, and the button should say so.
    confirmLabel: 'Skip',
  });
  if (!accepted) return;
  emit('skip', node.id);
}

/** Rows, top to bottom. Derived so a new tier needs no change here. */
const tiers = computed(() => {
  const seen = [...new Set(props.nodes.map((n) => n.tier))];
  return seen.sort((a, b) => a - b);
});

/** How far a player has got in a branch, for the tag on its root card. */
const progress = (branch: string) => {
  const inBranch = props.nodes.filter((n) => n.branch === branch);
  return { done: inBranch.filter((n) => n.unlocked).length, total: inBranch.length };
};

// ─── Layout ──────────────────────────────────────────────────────────────────

/**
 * A node with its place on the grid.
 *
 * The node is spread in rather than nested under a field so the template still
 * loops over something that *is* a node — the card markup is unchanged by the
 * layout, which is the point of doing the layout out here.
 */
interface Placement extends ResearchNode {
  /** 1-based grid row. Row 1 is tier 1, and depth increases downward. */
  row: number;
  /** 1-based grid column lines. Column 1 is the tier captions. `columnEnd` is exclusive. */
  columnStart: number;
  columnEnd: number;
}

/**
 * The tidy-tree pass: every node's row and column span.
 *
 * A leaf takes one column. An internal node takes the columns of all its leaves,
 * which is the union of its children's spans and therefore contiguous — that is
 * the whole trick, and it is why `justify-self: center` is enough to put a
 * parent over the middle of its children. A blank column sits between sub-trees
 * so the four blocks read as four.
 *
 * It is written to survive data it disapproves of. A node with two prerequisites
 * is placed under the first one; anything the walk never reaches — an orphan, a
 * node in a cycle — is given a column of its own at the end rather than
 * disappearing, because a node the player can see in the API and not on the tab
 * is the worse failure.
 */
const layout = computed(() => {
  const byId = new Map(props.nodes.map((n) => [n.id, n]));
  const rowOf = (tier: number) => Math.max(1, tiers.value.indexOf(tier) + 1);

  // Sibling and root order is the order the server sent, which is already
  // branch, then tier, then `sortOrder` — the catalogue's own reading order.
  // Re-sorting here would need `sortOrder` on the wire, which is a second copy
  // of a decision the server has already made.
  const children = new Map<string, ResearchNode[]>();
  const roots: ResearchNode[] = [];
  for (const node of props.nodes) {
    const parent = node.prerequisiteIds.find((id) => byId.has(id));
    if (parent === undefined) roots.push(node);
    else children.set(parent, [...(children.get(parent) ?? []), node]);
  }

  const placements: Placement[] = [];
  const spacers: number[] = [];
  const placed = new Set<string>();
  // Column 1 is the tier captions, so the tree starts in column 2.
  let next = 2;

  const walk = (node: ResearchNode): void => {
    if (placed.has(node.id)) return;
    placed.add(node.id);
    // Pushed before the descent, so document order is reading order and Tab
    // walks the tree rather than bouncing between rows.
    const placement: Placement = {
      ...node,
      row: rowOf(node.tier),
      columnStart: next,
      columnEnd: next,
    };
    placements.push(placement);

    const kids = children.get(node.id) ?? [];
    if (kids.length === 0) next += 1;
    else for (const kid of kids) walk(kid);
    placement.columnEnd = next;
  };

  roots.forEach((root, index) => {
    if (index > 0) {
      spacers.push(next);
      next += 1;
    }
    walk(root);
  });

  for (const node of props.nodes) {
    if (placed.has(node.id)) continue;
    placed.add(node.id);
    placements.push({ ...node, row: rowOf(node.tier), columnStart: next, columnEnd: next + 1 });
    next += 1;
  }

  return { placements, spacers };
});

// ─── The viewport ────────────────────────────────────────────────────────────
//
// See the header comment. `.tree` is the port and `.grid` carries the transform,
// laid out exactly once at its natural size. Every number here is in one of two
// coordinate spaces and it is worth being blunt about which: **layout** pixels
// are the grid's own, and are what the connectors,
// the panel's placement and `pan-zoom.ts` all speak; **screen** pixels are what
// `getBoundingClientRect` returns, and are layout pixels times the zoom.

/** The scroll-free port the tree is read through. */
const viewEl = ref<HTMLElement | null>(null);
/** The laid-out grid. Its box is the content, and never changes with the zoom. */
const gridEl = ref<HTMLElement | null>(null);

/** Where the content's top-left corner sits in the port, in screen pixels. */
const pan = ref<Vec>({ x: 0, y: 0 });
/** How big it is drawn. 1 is the size the cards were designed at. */
const zoom = ref(1);
/** True only once a press has passed the threshold, for the cursor and the guards. */
const dragging = ref(false);

/** The content's own size, in layout pixels — independent of the zoom. */
function contentSize(): Size {
  const grid = gridEl.value;
  return grid ? { w: grid.offsetWidth, h: grid.offsetHeight } : { w: 0, h: 0 };
}

/** The port's size. `client*` rather than a rect, so it is never scaled. */
function viewportSize(): Size {
  const view = viewEl.value;
  return view ? { w: view.clientWidth, h: view.clientHeight } : { w: 0, h: 0 };
}

/**
 * How much of the port's bottom-right corner `.controls` actually covers.
 *
 * `.controls` is `position: absolute` over `.tree`, outside the grid's own
 * transform (see this section's opening note on the two coordinate spaces),
 * so nothing in `pan-zoom.ts`'s clamp maths knows it is there — a drag, a
 * wheel-zoom or a pinch could push the tree's own bottom-right corner exactly
 * underneath it. Sized to the *compact* cluster, the larger of the two
 * (`.garage.compact .controls button`'s own comment: floored at 44px, "the
 * cluster grows to ~180px" wide, plus the container's own padding and border
 * for the height) — a few extra pixels of reserve on desktop's smaller
 * cluster costs nothing next to a tab whose fit ratio is ~0.39 on a laptop.
 */
const CONTROLS_CLEARANCE: Size = { w: 190, h: 60 };

/**
 * The port's size with that corner walled off. Every pan/zoom operation feeds
 * this to `pan-zoom.ts` instead of the raw box — `clampPan`'s bound on the
 * high-coordinate side of each axis is `viewport − content·zoom`, so reporting
 * a smaller viewport reserves exactly that much empty space at the true box's
 * right and bottom edges, however the size arrived there: dragged, zoomed, or
 * revealed. `fit()` alone keeps the raw box (below) — a fitted tree is
 * centred, never pinned to a corner, so it is never what puts a card here.
 */
function clampedViewport(): Size {
  const raw = viewportSize();
  return {
    w: Math.max(0, raw.w - CONTROLS_CLEARANCE.w),
    h: Math.max(0, raw.h - CONTROLS_CLEARANCE.h),
  };
}

const view = () => ({ pan: pan.value, zoom: zoom.value });

function apply(next: { pan: Vec; zoom: number }): void {
  zoom.value = next.zoom;
  pan.value = next.pan;
}

/**
 * A slot's box in layout pixels.
 *
 * `offsetLeft`/`offsetTop` rather than a rect **because the offsets are already
 * layout pixels**: a slot's `offsetParent` is `.grid` (the only positioned
 * ancestor), and offsets are unaffected by a transform on an ancestor. So this is
 * the one measurement in the file that needs no division and cannot be wrong
 * about the zoom.
 */
function slotRect(slot: HTMLElement): Rect {
  return { x: slot.offsetLeft, y: slot.offsetTop, w: slot.offsetWidth, h: slot.offsetHeight };
}

/** The whole tree, centred, at 1:1 or the largest scale that shows all of it. */
function fit(): void {
  apply(fitView(contentSize(), viewportSize()));
}

/** The `+`/`−` controls: a zoom step about the middle of the port, since there is
 *  no pointer to zoom about when the gesture came from a button. */
function stepZoom(direction: 1 | -1): void {
  // The true box for the anchor — a button-driven zoom should centre on the
  // port the player actually sees — and the walled-off one for the clamp.
  const port = viewportSize();
  apply(
    zoomAbout(
      view(),
      { x: port.w / 2, y: port.h / 2 },
      clampZoom(zoom.value * (direction > 0 ? 1.25 : 1 / 1.25)),
      contentSize(),
      clampedViewport(),
    ),
  );
}

/**
 * The wheel zooms about the pointer, and must not scroll the page.
 *
 * Registered with `addEventListener(..., { passive: false })` in `onMounted`
 * rather than as `@wheel`, because `preventDefault` in a passive listener is
 * ignored — with a console warning at best — and Vue's template listeners are
 * passive by default in some setups. A tab that scrolled the garage every time
 * somebody tried to zoom would be the whole feature failing quietly.
 */
function onWheel(event: WheelEvent): void {
  const port = viewEl.value;
  if (!port) return;
  event.preventDefault();
  const box = port.getBoundingClientRect();
  apply(
    zoomAbout(
      view(),
      { x: event.clientX - box.left, y: event.clientY - box.top },
      wheelZoom(zoom.value, event.deltaY, event.deltaMode),
      contentSize(),
      clampedViewport(),
    ),
  );
}

/**
 * A press, in the three states it can be in.
 *
 * `moved` is what separates a click from a pan: until the pointer has travelled
 * `DRAG_THRESHOLD_PX` nothing moves and the press is still a click on whatever is
 * under it. The moment it does, the pointer is captured — so a pan that leaves
 * the tab, or the window, keeps working and cannot get stuck down.
 */
let drag: { id: number; from: Vec; pan: Vec; moved: boolean } | null = null;
/** Set when a drag ends, so the `click` it produces can be swallowed once. */
let dragEnded = false;

/**
 * The touch contacts currently on the port, and the pinch they may become.
 *
 * A phone has no wheel and no `+`/`−` a thumb wants to keep tapping, so two
 * fingers are the zoom — and the two-finger gesture is *also* the pan, which is
 * what two fingers mean on every map. The arithmetic is `pinchView`, computed
 * from the gesture's own start rather than incrementally (see `pan-zoom.ts`),
 * so `pinch` freezes the view and both starting points when the second finger
 * lands.
 *
 * One finger stays the ordinary drag through the machinery below: a touch
 * contact is `button 0` with `buttons 1`, so the pointer-event path needs no
 * touch branch of its own — `touch-action: none` on the port already keeps the
 * browser's scroll and its native pinch off these events.
 */
const touches = new Map<number, Vec>();
let pinch: { view: { pan: Vec; zoom: number }; a: Vec; b: Vec; ids: [number, number] } | null = null;

function startPinch(): void {
  const [aId, bId] = [...touches.keys()] as [number, number];
  // A press that was becoming a one-finger drag hands over to the pinch: the
  // gesture has been reclassified, not ended, so the click a moved drag would
  // have swallowed still has to be swallowed — `endPinch` does that.
  drag = null;
  dragging.value = true;
  pinch = {
    view: { pan: pan.value, zoom: zoom.value },
    a: touches.get(aId)!,
    b: touches.get(bId)!,
    ids: [aId, bId],
  };
  viewEl.value?.setPointerCapture(aId);
  viewEl.value?.setPointerCapture(bId);
}

/** A finger lifting ends the whole gesture; the survivor starts fresh if it
 *  moves again, which beats guessing which half of a pinch was "the drag". */
function endPinch(): void {
  if (!pinch) return;
  for (const id of pinch.ids) {
    if (viewEl.value?.hasPointerCapture(id)) viewEl.value.releasePointerCapture(id);
  }
  pinch = null;
  dragging.value = false;
  // Swallow the click the lifted finger produces, exactly as a moved drag does —
  // a pinch that happened to end over a card must not open its panel.
  dragEnded = true;
}

function onPointerDown(event: PointerEvent): void {
  if (event.pointerType === 'touch') {
    touches.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (touches.size === 2 && !pinch) {
      startPinch();
      return;
    }
    if (touches.size > 2) return; // a third finger changes nothing
  }
  // Left button only. The arena's right button is a look and this tab has no use
  // for a second gesture; a middle-button drag would also fight the platform's
  // own autoscroll for no gain. (A touch contact is button 0, so one finger
  // lands here and drags exactly as the mouse does.)
  if (event.button !== 0 || !event.isPrimary) return;
  dragEnded = false;
  drag = { id: event.pointerId, from: { x: event.clientX, y: event.clientY }, pan: pan.value, moved: false };
}

/**
 * Ends a press, whether it was a pan or a click.
 *
 * One place, because there are four ways out — the button coming up, the pointer
 * being cancelled, the capture being lost, and the release that never arrives at
 * all — and every one of them has to leave the same three things clean.
 */
function endDrag(pointerId: number, moved: boolean): void {
  dragEnded = moved;
  drag = null;
  dragging.value = false;
  if (viewEl.value?.hasPointerCapture(pointerId)) viewEl.value.releasePointerCapture(pointerId);
}

function onPointerMove(event: PointerEvent): void {
  if (event.pointerType === 'touch' && touches.has(event.pointerId)) {
    touches.set(event.pointerId, { x: event.clientX, y: event.clientY });
  }
  if (pinch) {
    const a = touches.get(pinch.ids[0]);
    const b = touches.get(pinch.ids[1]);
    if (a && b) apply(pinchView(pinch, a, b, contentSize(), clampedViewport()));
    return;
  }
  if (!drag || event.pointerId !== drag.id) return;
  // **A release that never arrived.** Drag out of the window and let go there and
  // the `pointerup` can simply not be delivered, which used to leave the tab in a
  // drag for ever: `dragging` stayed true so no panel would open again, and the
  // port kept the pointer capture that retargets every event away from the cards,
  // so nothing on the tab was clickable either. The first move with no button
  // held is the earliest this can be known, and it is enough. Proven by the e2e
  // case, which drags 4,000 px deliberately and then checks the tab still works.
  if (event.buttons === 0) {
    endDrag(drag.id, drag.moved);
    return;
  }
  const dx = event.clientX - drag.from.x;
  const dy = event.clientY - drag.from.y;
  if (!drag.moved) {
    if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
    drag.moved = true;
    dragging.value = true;
    viewEl.value?.setPointerCapture(drag.id);
  }
  pan.value = clampPan(
    { x: drag.pan.x + dx, y: drag.pan.y + dy },
    contentSize(),
    clampedViewport(),
    zoom.value,
  );
}

function onPointerUp(event: PointerEvent): void {
  if (event.pointerType === 'touch') {
    touches.delete(event.pointerId);
    if (pinch && pinch.ids.includes(event.pointerId)) {
      endPinch();
      return;
    }
  }
  if (!drag || event.pointerId !== drag.id) return;
  endDrag(drag.id, drag.moved);
}

/**
 * The capture going away for any other reason — a browser gesture taking over, a
 * page becoming hidden. Nothing calls this in the ordinary path, because
 * `endDrag` releases the capture itself and `drag` is already `null` by the time
 * the event lands.
 */
function onLostCapture(event: PointerEvent): void {
  // A touch pointer is *implicitly* captured by whatever it lands on, so the
  // explicit `setPointerCapture` on the port steals it from the card under the
  // finger — and the card's `lostpointercapture` bubbles up here looking exactly
  // like the gesture dying. The port still holding the capture is the tell that
  // the gesture is alive and ours; a mouse never takes an implicit capture,
  // which is why this only ever bit on touch, one move into every drag that
  // began on a card.
  if (viewEl.value?.hasPointerCapture(event.pointerId)) return;
  if (pinch && pinch.ids.includes(event.pointerId)) {
    touches.delete(event.pointerId);
    endPinch();
    return;
  }
  if (!drag || event.pointerId !== drag.id) return;
  endDrag(drag.id, drag.moved);
}

/**
 * Swallows the click a completed drag leaves behind.
 *
 * In the capture phase, so it never reaches a card or — the case that matters —
 * the panel's own Research button, where a stray click would open a purchase
 * dialog at the end of a pan. Exactly one click is eaten: `dragEnded` is cleared
 * here and again on the next press, so a press that never became a drag is a
 * perfectly ordinary click.
 */
function onClickCapture(event: MouseEvent): void {
  if (!dragEnded) return;
  dragEnded = false;
  event.stopPropagation();
  event.preventDefault();
}

/**
 * Undoes any scroll the browser did to the port.
 *
 * `overflow: hidden` still makes a box a scroll container, so focusing a card
 * that is off screen makes the browser scroll it into view — and then the
 * transform says the card is in one place while it is drawn in another, which is
 * the disagreement every measurement in this file would inherit. The reveal is
 * done as a *pan* instead (`onSlotFocus`), so the right response here is simply
 * to put the scroll back to zero. It is deliberately not folded into the pan: the
 * explicit reveal has already run by the time this fires, and adding the scroll
 * on top would move the tree twice.
 */
function onViewportScroll(): void {
  const port = viewEl.value;
  if (!port) return;
  if (port.scrollLeft !== 0) port.scrollLeft = 0;
  if (port.scrollTop !== 0) port.scrollTop = 0;
}

// ─── Edges ───────────────────────────────────────────────────────────────────

interface Edge {
  key: string;
  d: string;
  /** `done` — both ends unlocked. `open` — the prerequisite is in hand. */
  state: 'done' | 'open' | 'locked';
}

const edges = ref<Edge[]>([]);
const size = ref({ w: 0, h: 0 });

/**
 * Re-measures every card and rebuilds the connectors.
 *
 * Each one is a bracket: a stub down out of the parent, a horizontal spine
 * halfway down the gutter, and a stub down into each child. Drawn per *edge*
 * rather than per parent so an edge can carry its own state colour — the spine
 * segments of one family simply overdraw, which is what a tech tree's bracket
 * looks like anyway.
 *
 * The card rects come from the `.node` button rather than from its slot, because
 * the slot of a root also carries the branch tag above the card and an edge must
 * leave the card, not the caption.
 *
 * Cheap enough to run on every layout change — thirty-four cards, twenty-nine edges —
 * and cheaper than the alternative of keeping a parallel model of where the grid
 * put things.
 *
 * **Every rect here is divided by the zoom**, because the grid sits under a
 * scaled ancestor and a `getBoundingClientRect` is therefore in screen pixels
 * while the `<svg>` these paths go into is sized in the grid's own layout
 * pixels. That division is also why this does not have to run on a zoom step at
 * all: scaling every rect by `s` and dividing by `s` is the same measurement, so
 * the brackets are measured when the *data* or the *box* changes and are correct
 * at every scale. Re-laying the grid out per zoom step would have made them
 * wrong once per frame instead.
 */
function measure(): void {
  const grid = gridEl.value;
  if (!grid) return;

  const scale = zoom.value || 1;
  const base = grid.getBoundingClientRect();
  size.value = { w: base.width / scale, h: base.height / scale };

  const boxes = new Map<string, DOMRect>();
  for (const el of grid.querySelectorAll<HTMLElement>('[data-research]')) {
    const id = el.dataset.research;
    const card = el.querySelector('.node') ?? el;
    if (id) boxes.set(id, card.getBoundingClientRect());
  }

  const byId = new Map(props.nodes.map((n) => [n.id, n]));
  const unlocked = new Set(props.nodes.filter((n) => n.unlocked).map((n) => n.id));

  /** Children per parent, over *every* prerequisite: a second one still draws. */
  const kidsOf = new Map<string, string[]>();
  for (const node of props.nodes) {
    for (const requiredId of node.prerequisiteIds) {
      kidsOf.set(requiredId, [...(kidsOf.get(requiredId) ?? []), node.id]);
    }
  }

  const next: Edge[] = [];
  for (const [requiredId, kidIds] of kidsOf) {
    const from = boxes.get(requiredId);
    const rects = kidIds.map((id) => boxes.get(id)).filter((r): r is DOMRect => !!r);
    if (!from || rects.length === 0) continue;

    const x1 = (from.left + from.width / 2 - base.left) / scale;
    const y1 = (from.bottom - base.top) / scale;
    // One spine for the whole family, halfway between the parent and the
    // nearest child, so every bracket in a gutter has the same shoulder.
    const spine = ((from.bottom + Math.min(...rects.map((r) => r.top))) / 2 - base.top) / scale;

    for (const id of kidIds) {
      const to = boxes.get(id);
      const node = byId.get(id);
      if (!to || !node) continue;
      const x2 = (to.left + to.width / 2 - base.left) / scale;
      const y2 = (to.top - base.top) / scale;

      next.push({
        key: `${requiredId}->${id}`,
        d: `M ${x1} ${y1} V ${spine} H ${x2} V ${y2}`,
        state: node.unlocked ? 'done' : unlocked.has(requiredId) ? 'open' : 'locked',
      });
    }
  }

  edges.value = next;
  // An open panel is anchored to a card that has just moved.
  place();
}

const remeasure = () => void nextTick(measure);

// ─── The panel ───────────────────────────────────────────────────────────────

/** The node whose panel is open, or `null`. At most one is ever open. */
const openId = ref<string | null>(null);
/** Where the open panel sits, in `.grid`'s own coordinates. */
const panelPos = ref({ left: 0, top: 0 });
/** The slot the open panel is anchored to. Not reactive: only `place()` reads it. */
let anchorEl: HTMLElement | null = null;
/** The open panel itself, so its real size can be measured before clamping. */
let panelEl: HTMLElement | null = null;
/** Template ref, as a function: the panel is inside a `v-for`, so a named one
 *  would arrive as an array of the thirty-four slots that never hold anything. */
function setPanelEl(el: unknown): void {
  panelEl = el instanceof HTMLElement ? el : null;
}
let closeTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * How long the panel survives the pointer leaving.
 *
 * The panel overlaps its card by `OVERLAP_PX`, so a straight move into it never
 * leaves the slot at all and this never runs. The grace is for the diagonal
 * that clips the corner — without it the panel vanishes on the way to its own
 * button, which is exactly the gesture it exists for.
 */
const CLOSE_DELAY_MS = 140;
/** The panel starts inside its card's edge, so there is no gap to fall through. */
const OVERLAP_PX = 4;

function cancelClose(): void {
  if (closeTimer !== null) clearTimeout(closeTimer);
  closeTimer = null;
}

/**
 * Positions the open panel beside its card, clamped inside the grid.
 *
 * Right of the card by preference and left when that would run off the end. It
 * stays beside rather than below because the tree runs downward now: a panel
 * under the card would cover the children it is describing the way to, and the
 * bottom row would have nothing under it to open into. Every branch's last
 * lineage is against the right edge, so the flip is what keeps a panel there
 * reachable.
 *
 * Clamping rather than overflowing matters for a second reason: anything outside
 * the grid would enlarge the content the pan is bounded against, and a hover
 * would change where the tree can be dragged to. It used to be stated as *`.tree`
 * scrolls, so an overhang widens the scroll area* — the tab does not scroll any
 * more, but the promise is the same one and is still load-bearing.
 *
 * **The clamp is to the grid, not to what is on screen**, and that is unchanged
 * on purpose. Zoomed well in, a 16 rem panel is wider than the port and a card
 * near the edge opens one that runs past it — which wants a pan to read, exactly
 * as it wanted a scroll before. Clamping to the visible region instead would be a
 * real improvement and is not this change: the load-bearing promise is that a
 * panel never enlarges the content, because that is what the connectors are
 * measured from and what the pan is bounded against.
 *
 * **Every measurement here is an `offset*`, and that is the whole zoom story.**
 * `left` and `top` are written into the panel's own `style`, so they have to be
 * layout pixels; a `getBoundingClientRect` on the scaled grid is in screen
 * pixels, and mixing the two put the panel `1/zoom` of the way across the tree.
 * The slot's `offsetParent` is `.grid` and offsets ignore an ancestor's
 * transform, so this arithmetic is identical at every zoom and there is nothing
 * to divide.
 */
function place(): void {
  const grid = gridEl.value;
  if (!grid || !anchorEl || !panelEl || openId.value === null) return;

  const gridW = grid.offsetWidth;
  const gridH = grid.offsetHeight;
  const card = slotRect(anchorEl);
  const w = panelEl.offsetWidth;
  const h = panelEl.offsetHeight;

  let left = card.x + card.w - OVERLAP_PX;
  if (left + w > gridW) left = card.x - w + OVERLAP_PX;
  left = Math.max(0, Math.min(left, Math.max(0, gridW - w)));

  let top = card.y;
  top = Math.max(0, Math.min(top, Math.max(0, gridH - h)));

  panelPos.value = { left, top };
}

function openPanel(id: string, slot: EventTarget | null): void {
  // Mid-pan the pointer crosses card after card, and opening each one in turn
  // would flash a panel per card for the length of the drag. A drag is a
  // gesture about the *tree*, so it addresses no node.
  if (dragging.value) return;
  cancelClose();
  if (slot instanceof HTMLElement) anchorEl = slot;
  openId.value = id;
  // The panel does not exist until it renders, and its own height decides
  // whether it has to be nudged up, so placing it waits a tick.
  void nextTick(place);
}

/**
 * Focus opens the panel *and* brings the card into view.
 *
 * The second half is what the browser used to do for free: `.tree` scrolled, so
 * focusing a card off the right-hand end scrolled it in. Under a transform there
 * is nothing to scroll, so a keyboard user tabbing along a row would have been
 * moving a focus ring they could not see — thirty-four cards, most of them
 * unreachable in practice. `nearest`, so a card already on screen does not move
 * the tree and reading down a branch is not a lurch per keystroke.
 *
 * Pointer and click deliberately do **not** reveal: panning the tree under a
 * pointer that has just arrived on a card is the one thing a hover must never do.
 */
function onSlotFocus(id: string, slot: EventTarget | null): void {
  openPanel(id, slot);
  if (slot instanceof HTMLElement) {
    pan.value = panToReveal(view(), slotRect(slot), contentSize(), clampedViewport());
  }
}

function closeNow(): void {
  cancelClose();
  openId.value = null;
  anchorEl = null;
  panelEl = null;
}

function scheduleClose(): void {
  cancelClose();
  closeTimer = setTimeout(closeNow, CLOSE_DELAY_MS);
}

/** Focus leaving the card *and* its panel closes it; moving between them does not. */
function onFocusOut(event: FocusEvent): void {
  const slot = event.currentTarget;
  const next = event.relatedTarget;
  if (slot instanceof HTMLElement && next instanceof Node && slot.contains(next)) return;
  closeNow();
}

// ─── The shop's pointer ──────────────────────────────────────────────────────
//
// See `state/research-focus`. Two things happen when a focus arrives: the card
// is ringed (a class, read straight off `researchFocus` in the template) and the
// tree pans it into view.
//
// **The pan runs on the focus changing, never on a render.** The tree is about
// 2,068 px wide against a tab under half that, so being sent to a node really does
// mean moving the view — and doing it on every render would undo the one property
// the panel is built around: opening a card moves nothing. A `watch` on the id is
// the narrowest trigger there is.
//
// It is called from two places, and both are needed. The ordinary route is a
// player clicking a gate name on the hulls tab, which sets the focus and *then*
// switches tabs — so by the time this component exists the focus is already
// there and no watcher will ever fire for it. The watch covers a focus arriving
// while the tree is on screen, which is the contract rather than a route that
// exists today.

/** Puts the focused card on screen, once, after it has been laid out. */
function revealFocus(id: string | null): void {
  if (id === null) return;
  void nextTick(() => {
    const slot = gridEl.value?.querySelector<HTMLElement>(`[data-research="${CSS.escape(id)}"]`);
    if (!slot) return;
    // **Centred, unlike a focus reveal.** A player who followed a *Requires
    // Light Frames* link is being shown a node they have never seen, and the
    // middle of the port is where you show somebody something; a minimal nudge
    // would leave it against whichever edge it came in from. Bounded by
    // `clampPan` all the same, so a capstone lands as close to centre as the
    // tree's own edge allows. Nothing is animated — the case that proves this
    // would then be racing a transition.
    pan.value = panToReveal(view(), slotRect(slot), contentSize(), clampedViewport(), 'centre');
  });
}

watch(researchFocus, revealFocus);

let observer: ResizeObserver | null = null;
let portObserver: ResizeObserver | null = null;

onMounted(() => {
  remeasure();
  // The tab opens fitted rather than at the top-left corner, which is the point
  // of the change: on a laptop the first thing on screen is the shape of the
  // tree. `fitView` never magnifies, so a monitor wide enough for the whole grid
  // still sees it at the size the cards were designed at.
  void nextTick(fit);
  // A focus set before this component existed — the ordinary route in from the
  // shop. See the note above `revealFocus`. After the fit, or it would be undone
  // by it.
  void nextTick(() => revealFocus(researchFocus.value));
  // Fonts landing, a scrollbar appearing, the window changing width: all of it
  // moves the cards, and an edge that does not follow points at nothing. Note it
  // does *not* fire for a zoom step: a `ResizeObserver` watches the layout box,
  // which a transform leaves alone — which is exactly the property that lets the
  // brackets be measured once.
  observer = new ResizeObserver(() => measure());
  if (gridEl.value) observer.observe(gridEl.value);

  // **A window resize keeps the player's zoom and re-clamps the pan**, rather
  // than re-fitting. Re-fitting would yank a view somebody had deliberately set
  // the moment they resized the window or opened a sidebar; re-clamping is the
  // narrower promise this needs, which is that the tree cannot end up off screen
  // because the port shrank. Fit is one click away when a player does want it.
  portObserver = new ResizeObserver(() => {
    pan.value = clampPan(pan.value, contentSize(), clampedViewport(), zoom.value);
  });
  if (viewEl.value) {
    portObserver.observe(viewEl.value);
    // Non-passive, by hand. See the note on `onWheel`.
    viewEl.value.addEventListener('wheel', onWheel, { passive: false });
  }

  ticker = setInterval(() => {
    tickMs.value = Date.now();
    if (asked || !busyResearching.value) return;
    if (props.nodes.some((n) => n.inProgress && remainingOf(n) <= 0)) {
      asked = true;
      emit('elapsed');
    }
  }, 1000);
});

onBeforeUnmount(() => {
  observer?.disconnect();
  observer = null;
  portObserver?.disconnect();
  portObserver = null;
  viewEl.value?.removeEventListener('wheel', onWheel);
  if (ticker) clearInterval(ticker);
  ticker = null;
  cancelClose();
  // "Left the page." A highlight is the answer to a question that was asked on
  // another tab; leaving is the player having read it, and a ring still lit when
  // they wander back would be pointing at a node they no longer asked about.
  clearResearchFocus();
});

watch(
  () => props.nodes,
  () => {
    // A fresh answer resets what the countdown is measured from, and re-arms
    // the request for the next one.
    snapshotAtMs.value = Date.now();
    tickMs.value = snapshotAtMs.value;
    asked = false;

    // "Bought it." This read is the server's answer to whatever was last asked
    // for, so it is the one place a start can be seen to have landed. Cleared
    // either way — a refusal is an answer too, and holding the id would let a
    // later, unrelated start of the same node drop a highlight it never earned.
    if (startedId !== null) {
      if (props.nodes.find((n) => n.id === startedId)?.inProgress) {
        clearResearchFocusIf(startedId);
      }
      startedId = null;
    }

    remeasure();
  },
  { deep: true },
);
</script>

<template>
  <div
    ref="viewEl"
    class="tree"
    :class="{ dragging }"
    data-test="research-view"
    @pointerdown="onPointerDown"
    @pointermove="onPointerMove"
    @pointerup="onPointerUp"
    @pointercancel="onPointerUp"
    @lostpointercapture="onLostCapture"
    @click.capture="onClickCapture"
    @scroll="onViewportScroll"
  >
    <div
      ref="gridEl"
      class="grid"
      :style="{
        '--tiers': tiers.length,
        transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
      }"
      data-test="research-grid"
    >
      <svg
        class="edges"
        :viewBox="`0 0 ${size.w} ${size.h}`"
        :width="size.w"
        :height="size.h"
        aria-hidden="true"
      >
        <path
          v-for="edge in edges"
          :key="edge.key"
          :class="['edge', edge.state]"
          :d="edge.d"
        />
      </svg>

      <div
        v-for="(tier, index) in tiers"
        :key="`head-${tier}`"
        class="row-head"
        :style="{ gridRow: index + 1, gridColumn: 1 }"
      >
        <span class="tier-no">Tier {{ tier }}</span>
      </div>

      <div
        v-for="column in layout.spacers"
        :key="`gap-${column}`"
        class="subtree-gap"
        :style="{ gridColumn: column }"
        aria-hidden="true"
      ></div>

      <div
        v-for="node in layout.placements"
        :key="node.id"
        class="slot"
        :class="{ raised: node.id === openId, root: !node.prerequisiteIds.length }"
        :style="{ gridRow: node.row, gridColumn: `${node.columnStart} / ${node.columnEnd}` }"
        :data-research="node.id"
        :data-requires="node.prerequisiteIds.join(' ')"
        @mouseenter="openPanel(node.id, $event.currentTarget)"
        @mouseleave="scheduleClose"
        @focusin="onSlotFocus(node.id, $event.currentTarget)"
        @focusout="onFocusOut"
        @keydown.escape="closeNow"
        @click="onSlotClick(node, $event.currentTarget)"
      >
        <p v-if="!node.prerequisiteIds.length" class="branch-tag">
          <span class="branch-name">{{ node.branchName }}</span>
          <span class="count">
            {{ progress(node.branch).done }} / {{ progress(node.branch).total }}
          </span>
        </p>

        <button
          type="button"
          class="node"
          :class="{
            unlocked: node.unlocked,
            blocked: !node.available && !node.unlocked,
            ready: node.available && !node.unlocked && !node.inProgress,
            running: node.inProgress,
            open: openId === node.id,
            focused: researchFocus === node.id,
          }"
          :aria-expanded="openId === node.id"
          :aria-controls="`research-panel-${node.id}`"
          :data-action="`open-research-${node.id}`"
          :data-test="researchFocus === node.id ? 'research-focused' : undefined"
        >
          <!-- The border, drawn as a progress bar. See `.progress` in the styles. -->
          <svg
            v-if="node.inProgress"
            class="progress"
            :data-test="`research-progress-${node.id}`"
            :data-progress="progressOf(node).toFixed(3)"
            aria-hidden="true"
          >
            <rect
              class="progress-fill"
              x="0"
              y="0"
              width="100%"
              height="100%"
              rx="2"
              pathLength="1"
              :style="{ strokeDasharray: `${progressOf(node).toFixed(4)} 1` }"
            />
            <rect
              class="progress-glare"
              x="0"
              y="0"
              width="100%"
              height="100%"
              rx="2"
              pathLength="1"
            />
          </svg>

          <span class="art">
            <img
              v-if="artFor(node.id)"
              :src="artFor(node.id)!"
              alt=""
              loading="lazy"
              decoding="async"
              @error="noteMissing(node.id)"
            />
            <span v-else class="plate" aria-hidden="true">{{ initials(node.name) }}</span>
          </span>

          <span class="name">{{ node.name }}</span>

          <span v-if="node.unlocked" class="status badge">Researched</span>
          <span
            v-else-if="node.inProgress"
            class="status badge running"
            data-test="research-remaining"
          >
            {{ remainingLabel(node) }}
          </span>
          <span v-else class="status cost" :class="{ short: !node.affordable }">
            {{ formatCredits(node.cost) }} <CurrencyIcon kind="credits" />
          </span>
        </button>

        <aside
          v-if="openId === node.id"
          :id="`research-panel-${node.id}`"
          :ref="setPanelEl"
          class="panel"
          :style="{ left: `${panelPos.left}px`, top: `${panelPos.top}px` }"
        >
          <h4>{{ node.name }}</h4>
          <p class="blurb">{{ node.blurb }}</p>

          <p v-if="node.passive" class="passive">{{ node.passive }}</p>

          <p v-if="node.prerequisiteIds.length" class="meta needs">
            Needs
            <strong v-for="(id, index) in node.prerequisiteIds" :key="id">
              {{ nameOf(id)
              }}<span v-if="index < node.prerequisiteIds.length - 1">, </span>
            </strong>
          </p>

          <p v-if="node.unlocksItemIds.length || node.unlocksPartNames.length" class="meta">
            Unlocks
            <em v-for="(id, index) in node.unlocksItemIds" :key="id">
              {{ itemName(id)
              }}<span
                v-if="index < node.unlocksItemIds.length - 1 || node.unlocksPartNames.length"
                >,
              </span>
            </em>
            <em v-for="(name, index) in node.unlocksPartNames" :key="name" class="part">
              {{ name }}<span v-if="index < node.unlocksPartNames.length - 1">, </span>
            </em>
          </p>

          <p v-if="!node.unlocked && !node.inProgress" class="meta time">
            Takes {{ formatCountdown(node.durationSeconds) }}
          </p>
          <p v-else-if="node.inProgress" class="meta time">{{ remainingLabel(node) }} left</p>

          <button
            v-if="!node.unlocked && !node.inProgress"
            type="button"
            :data-action="`research-${node.id}`"
            :disabled="!node.available || credits < node.cost || busyId !== null || busyResearching"
            @click="startResearch(node)"
          >
            {{
              !node.available
                ? 'Prerequisites missing'
                : credits < node.cost
                  ? 'Not enough credits'
                  : busyResearching
                    ? 'Another research is running'
                    : 'Research'
            }}
          </button>

          <button
            v-if="node.inProgress"
            type="button"
            class="skip"
            :data-action="`skip-research-${node.id}`"
            :disabled="busyId !== null"
            @click="skipWait(node)"
          >
            Finish now — {{ node.skipDiamondCost }} <CurrencyIcon kind="diamonds" />
          </button>
        </aside>
      </div>
    </div>

    <div class="controls" data-test="research-view-controls">
      <button type="button" data-action="tree-zoom-out" aria-label="Zoom out" @click="stepZoom(-1)">
        −
      </button>
      <span class="level" data-test="research-zoom">{{ Math.round(zoom * 100) }}%</span>
      <button type="button" data-action="tree-zoom-in" aria-label="Zoom in" @click="stepZoom(1)">
        +
      </button>
      <button type="button" class="fit" data-action="tree-fit" @click="fit()">Fit</button>
    </div>
  </div>
</template>

<style scoped>
/* The port.
 *
 * `overflow: hidden` rather than `auto`: this is a map now, panned and zoomed,
 * and the two scrollbars it used to grow on a laptop were the thing this change
 * removes. Note a hidden box is still a *scroll container*, which is why
 * `onViewportScroll` exists — the browser will scroll one to reveal a focused
 * card, and the transform would then disagree with the screen.
 *
 * **A port has to have a height of its own, and `100%` is not available** — that
 * is the one part of this layout that had to be measured rather than reasoned
 * about, so both halves are written down.
 *
 * The grid is **out of flow** because in flow it sizes the port to the
 * *untransformed* tree, and the overflow this change removes comes straight back
 * one level up: measured, a 724 px-tall port inside a 720 px window, and under
 * 900 px — where the tab body becomes one `1fr` column whose automatic minimum is
 * its content — a 2,068 px-wide port with the whole tab scrolling sideways.
 *
 * And the height is `calc(100vh - 7rem)` rather than `100%` because the port's
 * parent is a `<div>` in `GarageScreen`'s column-flex tab body with no
 * `flex-grow`, so its height is its content's and a percentage against it is
 * indefinite — it silently becomes `auto`, and the port collapses to
 * `min-height`. What *is* stable is that the chrome above and below is a fixed
 * number of pixels rather than a fraction of the window: measured at 99 px on
 * this layout (a 59 px title bar and the body's 2 × 1.25 rem gutter), so
 * subtracting a constant tracks the available space at every window size. 7 rem
 * leaves ~13 px of slack, which is what keeps a rounding difference from
 * producing the outer scrollbar the whole change is about. If that bar ever
 * changes height the port is a few pixels out — and the failure is a scrollbar on
 * the tab, not a broken tree.
 *
 * `touch-action: none` is what buys touch and trackpad drag for nothing: without
 * it the browser claims the gesture to scroll an ancestor. Pinch-zoom is
 * deliberately not implemented, and the steppers cover the same ground. */
.tree {
  position: relative;
  height: calc(100vh - 7rem);
  min-height: 22rem;
  overflow: hidden;
  padding: 0;
  touch-action: none;
  cursor: grab;
  /* **Always, not only while dragging.** A press starts a text selection before
     the threshold decides it was a drag, so a class applied when the drag begins
     is applied too late — measured: one drag left the whole tree highlighted
     blue, which is also how a drag can end without a `click`. There is nothing
     here worth copying out, and the panel's prose is a description of a node
     rather than a document. */
  user-select: none;
  /* Its own stacking context. The grid stacks internally — connectors under
     cards, an open panel over everything — and without this those layers
     compete with the garage's sticky tab bar, which the column headers then
     scroll *over* instead of under. */
  z-index: 0;
  isolation: isolate;
}

/* Under 900 px the tab body stacks the sidebar above the tree and scrolls, so the
   port is one of two things sharing the window rather than the whole of it. A
   port taller than what is left is a port you have to scroll the page to see. */
@media (max-height: 640px), (max-width: 900px) {
  .tree {
    height: 60vh;
  }
}

/*
 * A phone held sideways (`.garage.compact` — `GarageScreen`'s one `matchMedia`
 * answer): the port is exactly what is visible, no more.
 *
 * At 667×375 the 60vh tier above computes to 225px and the standing
 * `min-height: 22rem` overrules it at 352px — against 280px of actual content
 * area — so the port ran 52px below the fold and took its own bottom-right zoom
 * controls with it: the only zoom a touchscreen has, reachable only by
 * scrolling a page whose subject is a map that pans. The height is derived from
 * the same `--bar-h` the lobby pads by (the bar's measured height, published by
 * a ResizeObserver on `.garage`) less the body's own 1.25rem top and bottom
 * gutters, so a taller bar moves the port instead of putting this figure out.
 */
.garage.compact .tree {
  height: calc(100vh - var(--bar-h, 55px) - 2.5rem);
  min-height: 14rem;
}

.tree.dragging {
  cursor: grabbing;
}

.grid {
  /* **Absolute, and that is load-bearing** — see the note on `.tree`: in flow it
     sizes the port to the untransformed tree and the overflow it was meant to
     remove reappears one level up. It is still the panel's containing block and
     still every slot's `offsetParent`, which is all the rest of this file asks of
     it. */
  position: absolute;
  top: 0;
  left: 0;
  /* The pan and the zoom, in one property. Top-left origin so the transform is
     exactly `screen = pan + layout × zoom`; anything else (`center`, the
     default) puts a size-dependent term in the middle of that identity and every
     measurement in the component has to carry it. */
  transform-origin: 0 0;
  /* Content-sized, so `offsetWidth` really is the tree's own width — which is
     what the pan is bounded against and what Fit reads. */
  width: max-content;
  display: grid;
  /* One card width for the whole tab. The branch tag reads it too — see the
     note above `.branch-tag` for why the two must never disagree. */
  --card-width: 7.4rem;
  /* One row per tier, spelled out rather than left implicit, because the
     sub-tree spacers span `1 / -1` and `-1` means the end of the *explicit*
     grid. With no explicit rows a spacer would collapse to a single line. */
  grid-template-rows: repeat(var(--tiers), max-content);
  /* Columns are implicit and assigned by the layout pass: one per leaf, plus a
     spacer between sub-trees and the tier captions in column 1. There are as
     many as the catalogue's shape asks for, which is not a number this file
     should be spelling out.

     **Columns hug their cards.** `max-content` rather than a fraction, or a
     wide monitor would stretch every column to fill the tab and drag the card
     with it — a 5 rem picture floating in the middle of a 23 rem box. A card is
     a picture with a caption; it has a natural size, and the column should be
     that size. The tree is as wide as it needs to be and no wider, which also
     keeps every bracket short. */
  grid-auto-columns: max-content;
  /* The *row* gap is the gutter the brackets are drawn in now; the column gap
     is only the air between two cards standing side by side. */
  gap: 2.6rem 0.55rem;
  justify-content: start;
  align-content: start;
}

/* ── edges ──────────────────────────────────────────────────────────────── */

.edges {
  position: absolute;
  inset: 0;
  /* Behind the cards: an edge crossing a row must pass under it, and the paths
     must never eat a click meant for a button. */
  z-index: 0;
  pointer-events: none;
  overflow: visible;
}

/* **Everything structural on this tab is drawn at 2 px.** The tree was built at
   the hairline weight the rest of the garage uses for panel chrome, and at the
   zoom levels the tab actually gets used at — Fit puts the whole tree in a port
   a third of its natural width — a 1 px connector between two 1 px cards
   disappears into the background entirely. The card border, this stroke and the
   tier rule were doubled together, because the three read as one drawing and
   thickening any one of them alone makes the other two look broken. */
.edge {
  fill: none;
  stroke-width: 3;
  stroke-linecap: round;
  /* A bracket is two right angles. Mitred corners spike at the shoulder. */
  stroke-linejoin: round;
}

.edge.locked {
  stroke: var(--steel-700);
  stroke-dasharray: 3 5;
}

.edge.open {
  stroke: var(--accent);
  opacity: 0.8;
}

.edge.done {
  stroke: #4caf6d;
  opacity: 0.7;
}

/* ── headers ────────────────────────────────────────────────────────────── */

/* One caption per tier, down the left edge, with the rule beside it rather than
   under it — the transpose of the header strip this used to be. It stretches to
   its row's full height so the rule marks the whole tier, and centres its text
   in that so a row made taller by a branch tag does not leave the label
   floating at the top of it. */
.row-head {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 0.6rem;
  border-right: 2px solid var(--steel-700);
  font-size: 0.65rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--steel-400);
  white-space: nowrap;
}

/* ── slots ──────────────────────────────────────────────────────────────── */

/* A grid item takes `z-index` without being positioned, and it must stay
   unpositioned: the panel is absolute against `.grid`, and a positioned slot
   would trap it inside one column.

   `justify-self: center` is the layout. A node spans the grid columns of its
   whole sub-tree, so centring it in that span puts it over the middle of its
   children — no measurement, no assumed card width.

   `align-self: start` is the other axis and is not the layout. It keeps the
   slot the card's own box: stretched to the row's height — which a two-line
   name or a branch tag sets — the slot would be a tall box with the card at the
   top of it, and the e2e case measures slots as well as cards. Nothing on
   screen moves either way, which is exactly why it is worth stating. */
.slot {
  z-index: 1;
  justify-self: center;
  align-self: start;
}

/* The slot holding the open panel comes to the front. Every one is its own
   stacking context, so without this a panel would slide under the card below. */
.slot.raised {
  z-index: 5;
}

/* Air between the five sub-trees. A whole column rather than a margin, so it
   cannot land inside a spanning node's area and pull it off centre. The width
   is on the element rather than on the track because the track is implicit and
   `max-content`, so an empty div would size it to nothing. */
.subtree-gap {
  grid-row: 1 / -1;
  width: 1.1rem;
}

/* ── the branch tag ─────────────────────────────────────────────────────── */

/* **The tag must not move the card it sits on**, and on this axis it does not.
 *
 * Left to right, the tag was a real problem: a root's slot carries it *above*
 * the card, alignment centres the margin box, and centring `tag + card` in the
 * sub-tree's row span pushed the card half a tag's height below the children it
 * was supposed to face — 8 px, and `research-tree.mjs` failed on it. It took a
 * `margin-bottom` of exactly the tag's own footprint to cancel.
 *
 * Top to bottom the alignment axis is horizontal, and the tag adds nothing to
 * the card's *width*: the two are both 7.4 rem, so the slot is 7.4 rem wide and
 * the card is already on its centre line. The cancelling margin is therefore
 * **zero**, and that is the re-derivation rather than a rotation of 1.2 rem — a
 * `margin-right` of a tag's height would push every root card off centre by
 * exactly the amount the old rule existed to remove.
 *
 * What the tag does now is make row 1 taller, which is harmless because a root
 * is by definition tier 1 and tier 1 is row 1: no row ever mixes a card that
 * carries a tag with one that does not. If that ever stops being true, the fix
 * is to line the *cards* up rather than to reach for a margin.
 *
 * The one property that must hold is that the tag stays no wider than the card,
 * so the two share `--card-width` and cannot drift apart. `.slot.root` carries
 * no geometry at all any more; it stays as the marker that says which cards
 * open a branch. */
.branch-tag {
  display: flex;
  width: var(--card-width);
  height: 0.9rem;
  margin: 0 0 0.3rem;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.4rem;
}

.branch-name {
  font-size: 0.6rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--steel-200);
}

.count {
  font-family: ui-monospace, 'SF Mono', monospace;
  font-size: 0.62rem;
  color: var(--steel-400);
}

/* ── the card ───────────────────────────────────────────────────────────── */

.node {
  position: relative;
  display: flex;
  /* Just wider than the picture: 5 rem of art plus the padding either side,
     with enough slack that a two-word name wraps to two lines rather than
     three. Fixed rather than `max-content`, or every card in a row would be a
     different width and the grid would read as ragged. */
  width: var(--card-width);
  flex-direction: column;
  align-items: center;
  gap: 0.3rem;
  padding: 0.55rem 0.45rem 0.5rem;
  /* 2 px, like every rule and connector on this tab — see the note on `.edge`.
     Held in a variable because the running node's progress bar is drawn *on* this
     edge and has to know how wide it is. */
  --node-border: 2px;
  border: var(--node-border) solid var(--steel-700);
  border-radius: 3px;
  background: var(--steel-800);
  font-family: inherit;
  text-align: center;
  cursor: pointer;
}

/* Researched: a border all the way round rather than a left edge, and a wash
   behind it — the same green, at the same low strength `.message.notice` and
   `.status.notice` already prove sits well against this steel. */
.node.unlocked {
  border-color: #4caf6d;
  background: rgba(76, 175, 109, 0.1);
}

/* Available to research: a full ring in the accent, no wash — the accent is
   already the loudest colour on the tab and a tint behind it would compete
   with the cost figure printed on the card itself. */
.node.ready {
  border-color: var(--accent);
}

/* Not yet available: the dimming stays, but opacity alone left a blocked card
   reading as merely "a bit quieter" rather than "not for you yet" — a second,
   darker layer on top says the second thing without dropping opacity further
   and taking the card's own text down with it. */
.node.blocked {
  opacity: 0.65;
}

.node.blocked::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: rgba(0, 0, 0, 0.32);
  pointer-events: none;
}

/* ── the running node's border ────────────────────────────────────────────────
 *
 * A node with a clock running on it is the one thing on the tab that is
 * changing, so it is the one thing that says *how far through it is* rather
 * than merely that it is busy. The border is the bar: the card's own 2 px edge
 * is the empty track, dimmed to a third of the amber, and the filled part is
 * drawn over it.
 *
 * **It is an SVG rect and not a `conic-gradient`, and that is the whole reason
 * this rule was rewritten.** A conic maps *angle*, so on a card two thirds as
 * wide as it is tall the sweep crawls down the long edges and leaps across the
 * corners — which is fine for a decorative glare, which is what the old rule
 * was, and is a lie the moment the arc is supposed to mean 40%. `pathLength="1"`
 * normalises the rect's own perimeter to 1, so `stroke-dasharray: 0.4 1` is
 * exactly forty per cent of the way round, measured in distance travelled.
 *
 * The geometry is arranged so no number has to be measured in JS. The `<svg>` is
 * inset 1 px — half the border — and the rect is its full box, so a 2 px stroke
 * centred on that path covers the border ring exactly. `rx: 2` is the card's
 * 3 px outer radius less the 1 px inset, which is the radius of the border's own
 * centre line.
 *
 * The travelling glow is a second rect on the same path: one short dash, moved
 * by `stroke-dashoffset` rather than by `transform: rotate()`, so it runs *along*
 * the border at a constant speed instead of sweeping through it. It is the only
 * animated part, and reduced motion drops it entirely — the fill is still the
 * whole of the information. */
.node.running {
  border-color: rgb(217 164 65 / 38%);
}

.node.running .progress {
  position: absolute;
  /* An absolutely positioned child is laid out against its ancestor's *padding*
     box, which on a bordered card starts one whole border in from the edge. So
     the offsets step back out over the border and in again by half of it, and
     the size adds that border back — landing the svg's box on the border's
     centre line, where a stroke of the same width, which straddles its path,
     covers the ring exactly. All four written against `--node-border`, so
     changing the card's edge cannot leave the bar behind it.

     **The width and height are not optional, and leaving them out fails in the
     one way a screenshot will not show.** An `<svg>` is a *replaced* element: at
     `width: auto` it takes its own **intrinsic** size — 300x150 px, the CSS
     default for an svg with no `width` attribute and no `viewBox` — rather than
     stretching between `left` and `right` the way an ordinary box would. So the
     bar was drawn 300 px wide around a 118 px card, more than twice its width.
     It went unnoticed because the close-up that checked the geometry was taken
     at 2.5x zoom, where the card measures 296 px and the two very nearly agree. */
  left: calc(var(--node-border) / -2);
  top: calc(var(--node-border) / -2);
  width: calc(100% + var(--node-border));
  height: calc(100% + var(--node-border));
  /* The stroke straddles the path, so half of it lies outside the svg's box, and
     an SVG root clips to its viewport by default. */
  overflow: visible;
  pointer-events: none;
}

.node.running .progress rect {
  fill: none;
  stroke-width: var(--node-border);
  /* Square ends. A round cap adds half a stroke to each end of the dash, which
     at zero progress draws a blob where nothing has happened yet. */
  stroke-linecap: butt;
}

.progress-fill {
  stroke: #d9a441;
}

/* One short dash, travelling. `stroke-dasharray` is in the same normalised
   units as the fill above, so the dash is 9% of the perimeter whatever shape
   the card ends up. */
.progress-glare {
  stroke: #fbe0a8;
  stroke-dasharray: 0.09 0.91;
  animation: node-crawl 2.6s linear infinite;
}

@keyframes node-crawl {
  from {
    stroke-dashoffset: 0;
  }
  to {
    stroke-dashoffset: -1;
  }
}

/* **Removed, not parked.** Stopping the animation leaves `stroke-dashoffset` at
   its initial 0, which puts the pale dash *at the path origin* — directly on top
   of the first 9% of the fill, which is where the fill starts. Below 9% progress
   the whole bar would then read as washed-out cream rather than amber, on the
   one setting whose users are least able to watch it move. The claim above is
   that the fill is the whole of the information; this is that claim
   implemented. */
@media (prefers-reduced-motion: reduce) {
  .progress-glare {
    display: none;
  }
}

/* **Hover and focus must not repaint a state.**
 *
 * This is the pale edge that says *the pointer is here* or *the keyboard is
 * here*, and it used to be unconditional — so hovering the one card in the tree
 * that was mid-research replaced its progress border with a flat grey one, and
 * the bar the player had just gone to look at disappeared under the act of
 * looking at it. `:not(.running)` rather than a reorder, because source order
 * between two equal-specificity rules is exactly the kind of fix that comes
 * undone the next time somebody moves a block. */
.node.open:not(.running),
.node:focus-visible:not(.running) {
  border-color: var(--steel-500, #6c7a89);
}

.node:focus-visible {
  outline: none;
}

/* A running node therefore needs the keyboard's answer somewhere else: a ring,
   in the same pale steel, costing no layout and taking nothing from the bar. */
.node.running:focus-visible {
  box-shadow: 0 0 0 2px rgb(108 122 137 / 70%);
}

/* **"This is the node you were sent to."**
 *
 * Deliberately none of the other three languages already on a card: not the
 * left-edge stripe, which says what *state* a node is in (green researched,
 * accent available, amber running), and not the pale border of hover and focus,
 * which says where the pointer or the keyboard is. This is a ring around the
 * whole card in a colour used nowhere else on the tab, because it answers a
 * different question — *where is it* — and a player scanning thirty-four cards for
 * an answer must not have to work out whether the one thing that stands out is
 * simply the one thing they are hovering.
 *
 * A ring rather than a border so it costs no layout: the cards are measured and
 * the connectors drawn from those rects, and a 2 px border would move a card
 * the moment the shop pointed at it. */
.node.focused:not(.running) {
  border-color: #7fb2ff;
}

.node.focused {
  box-shadow:
    0 0 0 2px rgb(127 178 255 / 65%),
    0 0 18px rgb(127 178 255 / 22%);
}

/* The picture is the node's identity, so it is the biggest thing on the card. */
.art {
  display: flex;
  width: 5rem;
  height: 5rem;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
  background: var(--steel-900, #12161c);
  overflow: hidden;
}

.art img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/* An honest placeholder rather than a broken image — the same fallback a map
   without art gets. */
.plate {
  font-family: ui-monospace, 'SF Mono', monospace;
  font-size: 1.1rem;
  letter-spacing: 0.06em;
  color: var(--steel-600);
}

.name {
  font-size: 0.78rem;
  line-height: 1.25;
  color: #fff;
  letter-spacing: 0.02em;
}

.status {
  font-family: ui-monospace, 'SF Mono', monospace;
  font-size: 0.72rem;
}

.badge {
  font-size: 0.6rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-family: inherit;
  color: #4caf6d;
}

/* Monospaced, or the card twitches every time a digit changes width. */
.badge.running {
  font-family: ui-monospace, 'SF Mono', monospace;
  font-size: 0.74rem;
  letter-spacing: 0;
  text-transform: none;
  color: #d9a441;
}

.cost {
  color: #fff;
}

.cost.short {
  color: #d9705f;
}

/* ── the panel ──────────────────────────────────────────────────────────── */

.panel {
  position: absolute;
  z-index: 6;
  display: flex;
  width: 16rem;
  flex-direction: column;
  gap: 0.45rem;
  padding: 0.7rem 0.8rem 0.75rem;
  border: 2px solid var(--steel-600);
  border-radius: 3px;
  /* Opaque, unlike the lobby's panels: this one lands on top of other cards
     rather than on a rendered room, and translucency here would read as two
     cards printed over each other. */
  background: var(--steel-800, #1b2027);
  box-shadow: 0 6px 22px rgb(0 0 0 / 55%);
}

.panel h4 {
  margin: 0;
  font-size: 0.82rem;
  color: #fff;
  letter-spacing: 0.03em;
}

.blurb {
  margin: 0;
  font-size: 0.72rem;
  line-height: 1.4;
  color: var(--steel-400);
}

.passive {
  margin: 0;
  font-family: ui-monospace, 'SF Mono', monospace;
  font-size: 0.7rem;
  color: #4caf6d;
}

.meta {
  margin: 0;
  font-size: 0.68rem;
  color: var(--steel-400);
}

.meta strong {
  color: var(--steel-200);
  font-weight: 500;
}

.meta em {
  font-style: normal;
  color: var(--accent);
}

.meta em.part {
  color: var(--steel-200);
}

.meta.time {
  font-family: ui-monospace, 'SF Mono', monospace;
  font-size: 0.66rem;
  color: var(--steel-400);
}

.panel button {
  margin-top: 0.15rem;
  padding: 0.42rem;
  border: 1px solid var(--steel-600);
  border-radius: 3px;
  background: var(--steel-700);
  color: var(--steel-200);
  font-size: 0.72rem;
}

.panel button:not(:disabled) {
  border-color: transparent;
  background: var(--accent);
  color: #12161c;
  font-weight: 600;
  cursor: pointer;
}

.panel button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.panel button.skip,
.panel button.skip:not(:disabled) {
  border-color: #d9a441;
  background: transparent;
  color: #d9a441;
  font-weight: 600;
}

/* ── the view controls ──────────────────────────────────────────────────────
 *
 * Bottom-right of the port, over the tree and above the panel's own layer, so a
 * card opened against that corner cannot bury them. Translucent and blurred like
 * the lobby's panels rather than opaque, because they sit on the map and are not
 * part of it. */
.controls {
  position: absolute;
  z-index: 7;
  right: 0.5rem;
  bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.15rem;
  padding: 0.2rem 0.3rem;
  border: 1px solid var(--steel-700);
  border-radius: 3px;
  background: rgb(18 22 28 / 78%);
  backdrop-filter: blur(6px);
  /* The port's cursor is a grab hand; over a button it should be a pointer. */
  cursor: default;
}

.controls button {
  min-width: 1.5rem;
  padding: 0.2rem 0.35rem;
  border: none;
  border-radius: 2px;
  background: none;
  color: var(--steel-200);
  font-family: inherit;
  font-size: 0.78rem;
  line-height: 1;
  cursor: pointer;
}

.controls button:hover,
.controls button:focus-visible {
  background: var(--steel-700);
  color: #fff;
}

.controls button.fit {
  margin-left: 0.15rem;
  padding-left: 0.45rem;
  border-left: 1px solid var(--steel-700);
  font-size: 0.62rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

/* Monospaced and fixed-width, or the row twitches between 35% and 100%. */
.controls .level {
  min-width: 2.6rem;
  font-family: ui-monospace, 'SF Mono', monospace;
  font-size: 0.64rem;
  text-align: center;
  color: var(--steel-400);
}

/*
 * ─── A phone held sideways ────────────────────────────────────────────────────
 *
 * Gated on `.garage.compact`, `GarageScreen`'s single `matchMedia` answer — the
 * `ItemCard.vue` / `ItemWorkbench.vue` discipline. Desktop never sees these.
 *
 * The steppers are not a convenience on touch, they are the *only* zoom: the
 * wheel needs a wheel and pinch is deliberately out of scope (see the controls'
 * own comment). Measured before this block they were 24×19 and Fit was 32×16 —
 * the two smallest targets in the whole garage, on the one control a phone
 * cannot do without. Floored at 44px; the cluster grows to ~180px of a corner
 * that holds nothing else.
 */
.garage.compact .controls button {
  min-width: 44px;
  min-height: 44px;
}

/*
 * The panel's own buttons — Research, and the diamond skip. These are layout
 * pixels inside the scaled grid, so they hold the floor at 100% zoom and shrink
 * with everything else below it; the honest zoom story on a phone is "zoom in,
 * then act", and the floored steppers above are what make that reachable.
 */
.garage.compact .panel button {
  min-height: 44px;
}
</style>
